/**
 * CSS Measurement Utilities
 *
 * Helpers for reading computed CSS values that can't be expressed as static
 * numbers — CSS custom properties and the iOS `env(safe-area-inset-*)` values.
 *
 * Used by the chat-scroll padding calculation (#672) so the last message isn't
 * clipped behind the home-indicator on iPhone X+ devices.
 */

/**
 * Read a CSS custom property from the document root as a pixel number.
 *
 * IMPORTANT: `getComputedStyle(...).getPropertyValue('--x')` returns the
 * *authored token* of a custom property (e.g. `"5rem"`), NOT a computed px
 * value — the browser does not collapse units for custom properties. So a naive
 * `parseFloat("5rem")` yields `5`, not `80`. This helper therefore resolves
 * `rem`/`em` against the root font-size so callers always get pixels.
 * Supported units: `px`, `rem`, `em`, and unitless. (`calc()`/`%` are NOT
 * resolved — use a probe element like `getSafeAreaInsetBottom` for those.)
 *
 * Returns `fallback` when the property is unset, doesn't parse to a finite
 * number, or when there's no document (defensive — this app is client-only).
 *
 * @param name CSS custom property name, e.g. `--oro-bottom-nav-height`
 * @param fallback Pixel value to return when the property can't be read
 */
export function getCssPxVar(name: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;

  const root = document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue(name).trim();
  if (!raw) return fallback;

  const num = Number.parseFloat(raw);
  if (!Number.isFinite(num)) return fallback;

  // rem/em both resolve against the root font-size here (we read from :root,
  // where em == rem). e.g. "5rem" -> 5 * 16 = 80px.
  if (/r?em$/.test(raw)) {
    const rootFontPx = Number.parseFloat(getComputedStyle(root).fontSize) || 16;
    return num * rootFontPx;
  }
  return num; // px or unitless — already pixels
}

/**
 * Measure the iOS bottom safe-area inset in pixels.
 *
 * `env(safe-area-inset-bottom)` can't be read directly from JS, so we append a
 * hidden, fixed-position probe whose height is set to that env value, measure
 * its rendered height, then remove it. Returns `0` on platforms without a
 * bottom inset (everything that isn't a notched/home-indicator iOS device) and
 * when there's no document.
 */
export function getSafeAreaInsetBottom(): number {
  if (typeof document === 'undefined') return 0;

  const probe = document.createElement('div');
  // Keep the probe out of layout/paint while it's briefly attached.
  probe.style.position = 'fixed';
  probe.style.bottom = '0';
  probe.style.left = '0';
  probe.style.width = '0';
  probe.style.height = 'env(safe-area-inset-bottom, 0px)';
  probe.style.pointerEvents = 'none';
  probe.style.visibility = 'hidden';

  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);

  return Number.isFinite(height) ? height : 0;
}
